// app/src/lib/revenue/sales.js — daily sales close + mix/trend (US-4.1, US-4.2).

import { db } from '../db.js';
import { requireUserId, cents } from '../procurement/util.js';
import { mutate } from '../scale/instrument.js';
import { ensureDefaults, create as createChannel } from './channels.js';
import { markBooksDirty } from '../backup/sharedSnapshot.js';
import { currentClaims } from '../auth/cognitoClient.js';
import { getKeywords as getOwnerDrawKeywords, isOwnerDraw } from '../cashflow/ownerDraw.js';
import { creditAccountIds, withoutCreditTxs } from '../cashflow/accounts.js';

// Deposit-descriptor → channel rules live in revenue/depositRules.js (a
// dependency-free module) so the cashflow rollups can share them — they must
// EXCLUDE already-netted clawbacks from expenses — without an import cycle
// (this module imports ownerDraw, which imports settledSales). The owner's
// custom descriptors are merged on top at sync time.
import { DEPOSIT_CHANNEL_RULES, isRevenueClawback, classifyChannel, depositRulesWithOwnerKeywords, matchDepositChannel, resolveChannelForRule } from './depositRules.js';
import { attributeStreamByDay, lastDayOfMonth, nextBankingDay, bankHolidaySetForRange } from './depositSchedule.js';

const MAX_DEPOSIT_KEYWORDS = 25;

// Owner-defined deposit descriptors are stored per-channel on userProfile (so
// they ride along in backups + the team shared snapshot) and merged into the
// matching built-in rule at sync time. One getter/setter pair per channel.
async function getDepositKeywords(field) {
  const c = await currentClaims();
  if (!c) return [];
  const p = await db.userProfile.get(c.sub);
  return p && Array.isArray(p[field]) ? p[field] : [];
}

async function setDepositKeywords(field, list) {
  const c = await currentClaims();
  if (!c) throw new Error('deposit-keywords: not signed in');
  const clean = [...new Set((list || []).map((s) => String(s).trim().toLowerCase()).filter(Boolean))].slice(0, MAX_DEPOSIT_KEYWORDS);
  const existing = (await db.userProfile.get(c.sub)) || { userId: c.sub };
  await db.userProfile.put({ ...existing, userId: c.sub, [field]: clean });
  return clean;
}

/** Owner-defined descriptors that identify CASH/CHECK deposits on their bank
 *  statement (merged into the built-in Cash rule). */
export const getCashDepositKeywords = () => getDepositKeywords('cashDepositKeywords');
export const setCashDepositKeywords = (list) => setDepositKeywords('cashDepositKeywords', list);

/** Owner-defined descriptors that identify CARD-PROCESSOR settlements on their
 *  bank statement (merged into the built-in Card rule) — e.g. their Square/Toast
 *  payout descriptor, which varies by processor and bank. */
export const getCardDepositKeywords = () => getDepositKeywords('cardDepositKeywords');
export const setCardDepositKeywords = (list) => setDepositKeywords('cardDepositKeywords', list);

/** Whole calendar days between two ISO dates (a − b). */
function daysBetweenIso(a, b) {
  return Math.round((new Date(a + 'T00:00:00Z') - new Date(b + 'T00:00:00Z')) / 86400000);
}

const dowMonIdx = (iso) => (new Date(iso + 'T00:00:00Z').getUTCDay() + 6) % 7; // Mon=0..Sun=6

/**
 * [Mon..Sun] sales-mix weights learned from SALE-DATED entries (hand-typed closes
 * and POS imports). Used to split a bundled payout across the days it covers.
 * Only trustworthy with broad coverage — needs takings on ≥5 different weekdays,
 * else undefined, which makes the split even.
 * @returns {Promise<number[]|undefined>}
 */
async function saleDatedDowWeights(userId) {
  try {
    const rows = await db.salesEntries.where('userId').equals(userId)
      .filter((e) => e.source !== 'plaid').toArray();
    const totals = [0, 0, 0, 0, 0, 0, 0];
    for (const e of rows) totals[dowMonIdx(e.date)] += Math.max(0, e.netSalesCents || 0);
    return totals.filter((v) => v > 0).length >= 5 ? totals : undefined;
  } catch { return undefined; }
}

/**
 * Every recognized revenue deposit, per channel, netted for clawbacks:
 *   Map(channelId -> Map(payoutDate -> cents)).
 * Extracted so syncFromDeposits (which attributes these to sale days) and
 * reconcileAttribution (which audits the result) read the bank feed through
 * exactly the same rules — an audit that used its own copy could agree with a
 * broken sync.
 */
async function collectRecognizedDeposits(userId, channelForRule) {
  const ownerKw = await getOwnerDrawKeywords().catch(() => []);
  // Augment the built-in rules with the owner's own per-channel bank descriptors
  // (Cash teller/deposit labels, Card processor payout labels) so unusual
  // statement wording is still recognized as revenue.
  const [cashKw, cardKw] = await Promise.all([
    getCashDepositKeywords().catch(() => []),
    getCardDepositKeywords().catch(() => []),
  ]);
  const rules = depositRulesWithOwnerKeywords({ cashKeywords: cashKw, cardKeywords: cardKw });
  // Deposits land in DEPOSITORY accounts — exclude credit-card transactions so a
  // card CHARGE at a delivery merchant (e.g. the owner ordering DoorDash on the
  // card) can't be misread as sales revenue.
  const txs = withoutCreditTxs(
    await db.plaidTransactions.where('userId').equals(userId).toArray(),
    await creditAccountIds(userId),
  );
  const byDeposit = new Map();  // channelId → Map(payoutDate → cents in)
  const clawbacks = new Map();  // channelId → Array<{date, cents}> (negative)
  const addDeposit = (channelId, date, cents) => {
    let m = byDeposit.get(channelId);
    if (!m) { m = new Map(); byDeposit.set(channelId, m); }
    m.set(date, (m.get(date) || 0) + cents);
  };
  const addClawback = (channelId, date, cents) => {
    let a = clawbacks.get(channelId);
    if (!a) { a = []; clawbacks.set(channelId, a); }
    a.push({ date, cents });
  };
  // ── Pending deposits COUNT ────────────────────────────────────────────────
  // They used to be skipped outright, because a pending row re-posts under a new
  // id when it clears and counting both would double the day. But an ACH payout
  // is pending for its first day or two — exactly the newest money — so skipping
  // it made the current month's most recent sales invisible. With sale-dated
  // attribution that is severe: a Tuesday delivery payout carries Fri+Sat+Sun,
  // so a single pending row is three missing sale days, and a month whose only
  // delivery payout is still pending reports $0 delivery.
  //
  // Plaid links the cleared row to the pending one via pending_transaction_id,
  // but this book never stored it, so dedupe on the pair that identifies an ACH
  // credit in practice: same channel and same exact amount, within a few days.
  // A posted row always wins; the pending twin is dropped.
  const POSTED_MATCH_DAYS = 6;
  const HOLIDAYS = bankHolidaySetForRange('2020-01-01', '2035-12-31');
  const postedKey = new Map(); // `${channel}|${amount}` → [dates]
  for (const t of txs) {
    if (t.pending || !t.date) continue;
    const n = matchDepositChannel(t, rules);
    if (!n) continue;
    const k = `${n}|${t.amountCents || 0}`;
    if (!postedKey.has(k)) postedKey.set(k, []);
    postedKey.get(k).push(t.date);
  }
  const supersededByPosted = (t, channelName) => {
    const dates = postedKey.get(`${channelName}|${t.amountCents || 0}`);
    if (!dates) return false;
    return dates.some((d) => Math.abs(daysBetweenIso(d, t.date)) <= POSTED_MATCH_DAYS);
  };

  for (const t of txs) {
    const amt = t.amountCents || 0;
    // Owner draws/contributions (either direction) are capital, not revenue.
    if (isOwnerDraw(t, ownerKw)) continue;
    const channelName = matchDepositChannel(t, rules);
    if (!channelName) continue; // not a recognized revenue counterparty
    const ch = channelForRule(channelName);
    if (!ch || !t.date) continue;
    // The cleared row for this money is already in the feed — take that one.
    if (t.pending && supersededByPosted(t, channelName)) continue;
    // A PENDING row carries an authorization date, which can be a weekend or a
    // holiday; the money will actually post on the next banking day. Snap it, or
    // one weekend-dated pending row makes the whole channel look like a 7-day
    // processor and knocks every payout off its settlement schedule. Posted
    // dates are left alone — a genuinely weekend-dated POSTED row is the signal
    // that this really is a 7/7 next-day processor.
    const when = t.pending ? nextBankingDay(t.date, HOLIDAYS) : t.date;
    if (amt > 0) {
      // Only an EXPLICIT clawback (chargeback / error charge / reversal /
      // refund collection — see isRevenueClawback) nets against that day's
      // channel revenue. Processor fees, billing, app charges and any other
      // debit from the same counterparties stay EXPENSES: revenue must equal
      // raw deposits (sales tax is owed on sales; fees aren't deductible from
      // taxable sales).
      if (!isRevenueClawback(t)) continue;
      addClawback(ch.channelId, when, -amt);
      continue;
    }
    if (amt === 0) continue;
    addDeposit(ch.channelId, when, -amt); // Plaid convention: <0 = inflow (a deposit)
  }

  // ── Deposit date → SALE days ───────────────────────────────────────────────
  // A deposit is money for sales made days earlier, and one deposit can cover
  // several sale days (a Monday card deposit is Thu+Fri+Sat; a weekly delivery
  // payout is 7 days). Storing it on the posting date left every Saturday and
  // Sunday reading $0 and made month totals need a second correction at read
  // time. So we run each channel's payout schedule BACKWARDS here, once, and
  // store sale-dated entries — every screen that reads salesEntries by date is
  // then correct with no further adjustment.
  //
  // Bundled payouts are split by the restaurant's day-of-week sales mix when
  // hand-entered closes make one available (restaurants are Fri/Sat-heavy, so an
  // even split understates the weekend), otherwise evenly. The platform does not
  // itemize the bundle, so an even split is the honest default — never a guess
  // dressed up as data.
  // A clawback is money the platform took back OUT of a payout, so net it onto
  // the most recent payout at or before it — the sales that payout covered are
  // the ones being reversed. Keeping clawbacks out of the payout DATE stream also
  // matters mechanically: a Saturday chargeback would otherwise look like a
  // weekend-dated payout (flipping the whole channel off its banking-day
  // schedule) and its short gap would skew the weekly/daily cadence detection.
  for (const [channelId, list] of clawbacks) {
    const payouts = [...(byDeposit.get(channelId)?.keys() || [])].sort();
    for (const { date, cents } of list) {
      let anchor = date;
      for (const p of payouts) { if (p <= date) anchor = p; else break; }
      addDeposit(channelId, anchor, cents);
    }
  }
  return byDeposit;
}

/**
 * Derive daily sales from imported bank deposits (deposits = revenue model).
 * Each recognized inflow is run BACKWARDS through its channel's payout schedule
 * and recorded as `source:'plaid'` salesEntries on the SALE DAYS it paid for
 * (net = the deposit amount, no gross-up, no platform fee). One deposit can
 * therefore write several days, and a day can gather money from several
 * deposits. Idempotent: every run rebuilds the plaid rows from scratch, so it is
 * also the migration path off the old deposit-dated rows.
 * Never overwrites a manually-entered ('manual') close. Returns counts.
 */
export async function syncFromDeposits() {
  const userId = await requireUserId();
  await ensureDefaults();
  let channels = await db.channels.where('userId').equals(userId).toArray();
  // Resolve by CLASSIFICATION, not display name: an operator who renamed
  // "DoorDash" used to have that platform's deposits dropped silently.
  const byName = (n) => resolveChannelForRule(n, channels);
  // Ensure the synthetic 0-commission channels exist (Card = in-house card
  // settlements; Cash = cash/check deposits).
  // EVERY channel the rules can map to must exist, not just the two synthetic
  // ones. A deposit whose channel row is missing is dropped on the floor at the
  // `if (!ch)` guard below — silently. Books seeded before a platform existed
  // (or with the delivery channels deleted) therefore showed card and cash
  // correctly while delivery read $0 forever, with nothing to indicate why.
  // The commission rate is left to channels.create, which derives it from the
  // name (DoorDash/Uber 30%, Grubhub 25%); Card and Cash take 0.
  const SYNTHETIC = new Set(['Card', 'Cash']);
  const needed = [...new Set(DEPOSIT_CHANNEL_RULES.map((r) => r.channel))];
  let created = 0;
  for (const name of needed) {
    if (resolveChannelForRule(name, channels)) continue;
    await createChannel(SYNTHETIC.has(name) ? { name, commissionRateBps: 0 } : { name });
    created += 1;
  }
  if (created) channels = await db.channels.where('userId').equals(userId).toArray();

  const byDeposit = await collectRecognizedDeposits(userId, byName);

  const dowWeights = await saleDatedDowWeights(userId);
  const chById = new Map(channels.map((c) => [c.channelId, c]));
  const agg = new Map(); // `${channelId}|${saleDate}` → net cents
  for (const [channelId, dateMap] of byDeposit) {
    const deposits = [...dateMap.entries()].map(([date, amountCents]) => ({ date, amountCents }));
    const kind = classifyChannel(chById.get(channelId));
    const byDay = attributeStreamByDay(deposits, kind, { dowWeights });
    for (const [saleDate, cents] of Object.entries(byDay)) {
      if (!cents) continue; // a rounding-zero day is not a sale day
      agg.set(`${channelId}|${saleDate}`, cents);
    }
  }

  // Plaid-derived entries are a pure FUNCTION of plaidTransactions, so every run
  // rebuilds them. A derived row is dropped when this pass no longer produces it
  // — which also migrates books written by the old deposit-dated build, since
  // those rows sit on posting dates we no longer emit (no separate migration) —
  // or when the owner has since hand-entered a close for that channel-day.
  //
  // That second case is new and load-bearing: derived rows are now SALE-dated, so
  // they land on exactly the days a hand-typed close occupies. Leaving both would
  // count the day twice. The owner's own close wins and the derived slice is
  // removed. (The deposit's money for that day is therefore dropped, deliberately
  // — the close is the better number, and adding them would inflate revenue.)
  const allEntries = await db.salesEntries.where('userId').equals(userId).toArray();
  const byHand = new Set(allEntries.filter((e) => e.source !== 'plaid').map((e) => `${e.channelId}|${e.date}`));
  const derived = new Map();
  for (const e of allEntries) if (e.source === 'plaid') derived.set(`${e.channelId}|${e.date}`, e);

  let removed = 0;
  for (const [key, e] of derived) {
    if (agg.has(key) && !byHand.has(key)) continue;
    await db.salesEntries.delete(e.salesEntryId);
    derived.delete(key);
    removed++;
  }

  let written = 0;
  let droppedCents = 0; // derived money a hand close superseded — see the return value
  for (const [key, netCents] of agg) {
    if (byHand.has(key)) { droppedCents += netCents; continue; } // the close is the authority for that day
    const sep = key.indexOf('|');
    const channelId = key.slice(0, sep);
    const date = key.slice(sep + 1);
    const existing = derived.get(key);
    if (existing && existing.netSalesCents === netCents) continue; // unchanged
    await db.salesEntries.put({
      salesEntryId: existing?.salesEntryId || crypto.randomUUID(),
      userId, channelId, date,
      grossSalesCents: netCents, platformFeesCents: 0, netSalesCents: netCents,
      tipsCents: 0, refundsCents: 0, voidsCents: 0, compsCents: 0, discountsCents: 0,
      source: 'plaid',
    });
    written++;
  }
  if (written > 0 || removed > 0) { try { markBooksDirty(); } catch { /* non-fatal */ } }
  return { written, removed, days: agg.size, droppedCents };
}

/** @typedef {{ salesEntryId:string, userId:string, channelId:string, date:string, grossSalesCents:number, platformFeesCents:number, netSalesCents:number, tipsCents:number, refundsCents:number, voidsCents:number, compsCents:number, discountsCents:number, source:'manual'|'plaid'|'pos_import', notes?:string }} SalesEntry */

/** Record (or update) a daily close entry for a channel. Idempotent on (channelId, date). */
export async function recordClose(input) {
  const userId = await requireUserId();
  if (!input.channelId) throw new Error('sales.record: channelId required');
  if (!input.date || !/^\d{4}-\d{2}-\d{2}$/.test(input.date)) throw new Error('sales.record: date must be YYYY-MM-DD');

  const channel = await db.channels.get(input.channelId);
  if (!channel || channel.userId !== userId) throw new Error('sales.record: channel not found');

  const grossCents = cents(input.grossSales);
  const refundsCents = cents(input.refunds);
  const voidsCents = cents(input.voids);
  const compsCents = cents(input.comps);
  const discountsCents = cents(input.discounts);
  // Platform fees auto-derive from channel commission rate unless explicitly provided.
  const platformFeesCents = input.platformFees != null
    ? cents(input.platformFees)
    : Math.round((grossCents * (channel.commissionRateBps || 0)) / 10_000);
  // Net = gross − fees − refunds − voids − comps − discounts (cannot go negative).
  const netSalesCents = Math.max(0, grossCents - platformFeesCents - refundsCents - voidsCents - compsCents - discountsCents);

  // Find existing (channelId, date) to upsert.
  const existing = await db.salesEntries
    .where('[userId+channelId+date]').equals([userId, input.channelId, input.date])
    .first();
  /** @type {SalesEntry} */
  const row = {
    salesEntryId: existing?.salesEntryId || crypto.randomUUID(),
    userId,
    channelId: input.channelId,
    date: input.date,
    grossSalesCents: grossCents,
    platformFeesCents,
    netSalesCents,
    tipsCents: cents(input.tips),
    refundsCents,
    voidsCents,
    compsCents,
    discountsCents,
    source: input.source || 'manual',
    notes: input.notes,
  };
  const isCreate = !existing;
  return mutate(
    {
      entityType: 'salesEntry',
      entityId: row.salesEntryId,
      action: isCreate ? 'create' : 'update',
      before: isCreate ? undefined : existing,
      after: row,
    },
    async () => { await db.salesEntries.put(row); return row; },
  );
}

export async function remove(salesEntryId) {
  const userId = await requireUserId();
  const existing = await db.salesEntries.get(salesEntryId);
  if (!existing || existing.userId !== userId) return;
  await mutate(
    { entityType: 'salesEntry', entityId: salesEntryId, action: 'delete', before: existing },
    async () => { await db.salesEntries.delete(salesEntryId); },
  );
}

/**
 * Daily close summary for a given date: all channels with their entered values.
 * Channels with no entry are returned with zeroes so the UI can fill them in.
 */
export async function dayBreakdown(date) {
  const userId = await requireUserId();
  const channels = await db.channels.where('[userId+isActive]').equals([userId, 1]).toArray();
  const entries = await db.salesEntries.where('[userId+date]').equals([userId, date]).toArray();
  const byChannel = new Map(entries.map((e) => [e.channelId, e]));
  return channels.map((c) => {
    const e = byChannel.get(c.channelId);
    return {
      channelId: c.channelId,
      channelName: c.name,
      commissionRateBps: c.commissionRateBps,
      grossSalesCents: e?.grossSalesCents || 0,
      platformFeesCents: e?.platformFeesCents || 0,
      netSalesCents: e?.netSalesCents || 0,
      tipsCents: e?.tipsCents || 0,
      refundsCents: e?.refundsCents || 0,
      voidsCents: e?.voidsCents || 0,
      compsCents: e?.compsCents || 0,
      hasEntry: !!e,
    };
  });
}

/**
 * Mix + period totals (US-4.2).
 *
 * @param {{ from:string, to:string, comparePrior?:boolean }} window
 */
export async function mixAndTrend(window) {
  const userId = await requireUserId();
  if (!isoOk(window.from) || !isoOk(window.to)) throw new Error('sales.mix: invalid date range');
  if (window.from > window.to) throw new Error('sales.mix: from > to');

  const channels = await db.channels.where('userId').equals(userId).toArray();
  const channelById = new Map(channels.map((c) => [c.channelId, c]));

  const entries = await db.salesEntries
    .where('[userId+date]').between([userId, window.from], [userId, window.to], true, true)
    .toArray();

  const totals = new Map(); // channelId → totals
  let grandNet = 0;
  let totalRefunds = 0, totalVoids = 0, totalComps = 0;
  for (const e of entries) {
    const t = totals.get(e.channelId) || { netSalesCents: 0, grossSalesCents: 0, platformFeesCents: 0 };
    t.netSalesCents += e.netSalesCents || 0;
    t.grossSalesCents += e.grossSalesCents || 0;
    t.platformFeesCents += e.platformFeesCents || 0;
    totals.set(e.channelId, t);
    grandNet += e.netSalesCents || 0;
    totalRefunds += e.refundsCents || 0;
    totalVoids += e.voidsCents || 0;
    totalComps += e.compsCents || 0;
  }

  const mix = [];
  for (const [channelId, t] of totals) {
    const ch = channelById.get(channelId);
    mix.push({
      channelId,
      channelName: ch?.name || 'Channel',
      netSalesCents: t.netSalesCents,
      grossSalesCents: t.grossSalesCents,
      platformFeesCents: t.platformFeesCents,
      sharePct: grandNet > 0 ? t.netSalesCents / grandNet : 0,
    });
  }
  mix.sort((a, b) => b.netSalesCents - a.netSalesCents);

  let priorComparison = null;
  if (window.comparePrior) {
    const span = daysSpan(window.from, window.to) + 1;
    const priorFrom = shiftDays(window.from, -span);
    const priorTo = shiftDays(window.to, -span);
    const priorEntries = await db.salesEntries
      .where('[userId+date]').between([userId, priorFrom], [userId, priorTo], true, true)
      .toArray();
    const priorNet = priorEntries.reduce((s, e) => s + (e.netSalesCents || 0), 0);
    priorComparison = {
      from: priorFrom, to: priorTo, netSalesCents: priorNet,
      deltaPct: priorNet > 0 ? (grandNet - priorNet) / priorNet : null,
    };
  }

  return {
    from: window.from, to: window.to,
    grossNetCents: grandNet,
    refundsCents: totalRefunds,
    voidsCents: totalVoids,
    compsCents: totalComps,
    mix,
    priorComparison,
  };
}

function isoOk(s) { return typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s); }
function shiftDays(iso, n) { const d = new Date(iso + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0,10); }
function daysSpan(a, b) { return Math.round((new Date(b + 'T00:00:00Z') - new Date(a + 'T00:00:00Z')) / 86_400_000); }

/**
 * Audit the attribution: does the sale-dated ledger still hold every dollar the
 * bank actually paid?
 *
 * Attribution only ever MOVES money between days — a payout is split with
 * largest-remainder, so the parts sum exactly to the deposit. A month can
 * therefore be wrong (the schedule guessed the wrong day) while any multi-month
 * sum stays right: an error in July is offset by the opposite error in June or
 * August. Errors redistribute, they do not accumulate. The only true drift is at
 * the two edges of the window, and it is bounded by a few days of sales rather
 * than growing with time.
 *
 * There is exactly one way money can genuinely leave the ledger: a hand-entered
 * close takes over a channel-day and the derived slice for it is dropped (see
 * syncFromDeposits). THAT would accumulate, so it is reported here rather than
 * left silent.
 *
 * @param {{from?:string, to?:string}} [window] ISO bounds; omit for all time
 * @returns {Promise<{depositCents:number, attributedCents:number, driftCents:number,
 *   handEnteredCents:number, byMonth:Record<string,{depositCents:number, attributedCents:number}>}>}
 *   depositCents   — recognized bank deposits, on their POSTING dates
 *   attributedCents— derived salesEntries, on their SALE dates
 *   driftCents     — depositCents − attributedCents. Over all time this is the
 *                    money that never made it into the ledger and should be ~0;
 *                    over a partial window it also includes the legitimate edge
 *                    effect (deposits for sales before `from`, sales after `to`
 *                    not yet deposited), so judge a window against its edges.
 */
export async function reconcileAttribution(window = {}) {
  const userId = await requireUserId();
  const channels = await db.channels.where('userId').equals(userId).toArray();
  // Resolve by CLASSIFICATION, not display name: an operator who renamed
  // "DoorDash" used to have that platform's deposits dropped silently.
  const byName = (n) => resolveChannelForRule(n, channels);
  const inWindow = (d) => (!window.from || d >= window.from) && (!window.to || d <= window.to);

  const byDeposit = await collectRecognizedDeposits(userId, byName);
  const byMonth = {};
  const bucket = (m) => (byMonth[m] || (byMonth[m] = { depositCents: 0, attributedCents: 0 }));
  let depositCents = 0;
  for (const dateMap of byDeposit.values()) {
    for (const [date, cents] of dateMap) {
      if (!inWindow(date)) continue;
      depositCents += cents;
      bucket(date.slice(0, 7)).depositCents += cents;
    }
  }

  const entries = await db.salesEntries.where('userId').equals(userId).toArray();
  let attributedCents = 0, handEnteredCents = 0;
  for (const e of entries) {
    if (!inWindow(e.date)) continue;
    const c = e.netSalesCents || 0;
    if (e.source === 'plaid') { attributedCents += c; bucket(e.date.slice(0, 7)).attributedCents += c; }
    else handEnteredCents += c;
  }

  return { depositCents, attributedCents, driftCents: depositCents - attributedCents, handEnteredCents, byMonth };
}

/**
 * Explain, end to end, why a channel kind shows the money it shows.
 *
 * Every screen that displays a sales split wraps its work in a try/catch that
 * falls back to zero, so "no deposits", "threw", and "classified wrong" all look
 * identical on screen. This walks the same chain and reports each link, so a
 * wrong number can be traced to the step that produced it instead of guessed at.
 *
 * @param {{month?:string, kind?:'delivery'|'card'|'cash'|'pos'}} [opts]
 * @returns {Promise<object>} a plain object safe to console.log
 */
export async function diagnose(opts = {}) {
  const kind = opts.kind || 'delivery';
  const month = opts.month || new Date().toISOString().slice(0, 7);
  const out = { month, kind, ok: true, problems: [] };
  const problem = (p) => { out.ok = false; out.problems.push(p); };

  const userId = await requireUserId();
  const channels = await db.channels.where('userId').equals(userId).toArray();
  // Resolve by CLASSIFICATION, not display name: an operator who renamed
  // "DoorDash" used to have that platform's deposits dropped silently.
  const byName = (n) => resolveChannelForRule(n, channels);

  // 1. Channels of this kind, and how each one classifies.
  out.channels = channels.map((c) => ({
    name: c.name, type: c.type || '(none)', kind: classifyChannel(c), id: c.channelId,
  }));
  const mine = out.channels.filter((c) => c.kind === kind);
  if (!mine.length) problem(`no channel classifies as '${kind}' — check the type/name column above`);

  // 2. Bank rows the rules recognize for those channels.
  const byDeposit = await collectRecognizedDeposits(userId, byName);
  out.recognizedPayouts = mine.map((c) => {
    const m = byDeposit.get(c.id);
    const rows = m ? [...m.entries()].sort() : [];
    return { channel: c.name, count: rows.length, latest: rows.slice(-4).map(([d, v]) => `${d}: ${(v / 100).toFixed(2)}`) };
  });
  if (mine.length && out.recognizedPayouts.every((r) => r.count === 0)) {
    problem('the bank feed has no payouts matching these channels — the descriptors are not being recognized (DEPOSIT_CHANNEL_RULES), or the rows sit in a credit account');
  }

  // 2b. Pending rows for these channels — the newest money, and the thing that
  //     used to be silently discarded.
  const allTx = await db.plaidTransactions.where('userId').equals(userId).toArray();
  const rules = depositRulesWithOwnerKeywords();
  out.pendingRows = allTx.filter((t) => t.pending && (t.amountCents || 0) < 0)
    .filter((t) => { const n = matchDepositChannel(t, rules); return n && mine.some((c) => c.name === n || classifyChannel({ name: n }) === kind); })
    .map((t) => `${t.date} ${(-t.amountCents / 100).toFixed(2)} ${matchDepositChannel(t, rules)}`);

  // 3. What attribution wrote, on SALE dates, inside the month.
  const from = `${month}-01`, to = lastDayOfMonth(month);
  const entries = await db.salesEntries.where('[userId+date]').between([userId, from], [userId, to], true, true).toArray();
  const ids = new Set(mine.map((c) => c.id));
  const inKind = entries.filter((e) => ids.has(e.channelId));
  out.saleDaysInMonth = inKind.sort((a, b) => a.date.localeCompare(b.date))
    .map((e) => `${e.date} ${(e.netSalesCents / 100).toFixed(2)} (${e.source})`);
  out.totalCents = inKind.reduce((s, e) => s + (e.netSalesCents || 0), 0);
  if (mine.length && out.recognizedPayouts.some((r) => r.count > 0) && out.totalCents === 0) {
    problem(`payouts exist but no ${month} sale days were written — every covered day fell outside the month, or a hand-entered close superseded them (see droppedCents from syncFromDeposits)`);
  }
  return out;
}
